/**
 * Painel Admin — Configurações de Autenticação
 * Gerencia Google Sign-In, Magic Link, Senha, Turnstile e Segurança
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { showToast } from '../components/Toast';
import { Button, Textarea } from '../components/ui';

interface SettingMeta {
  value: string;
  hasValue: boolean;
  encrypted: boolean;
  updated_at?: string;
}

/**
 * Componente para configurar Google OAuth diretamente no tenant
 * (credenciais na tabela tenants — override sobre auth_settings)
 */
function TenantGoogleOAuth() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [secretConfigured, setSecretConfigured] = useState(false);

  useEffect(() => {
    api.get<any>('/admin/auth-settings/google-tenant').then((res: any) => {
      setClientId(res?.google_client_id || '');
      setSecretConfigured(!!res?.google_client_secret_configured);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const body: any = { google_client_id: clientId };
      // Só enviar secret se foi alterado (não é placeholder)
      if (clientSecret && clientSecret !== '***') {
        body.google_client_secret = clientSecret;
      }
      await api.put('/admin/auth-settings/google-tenant', body);
      showToast('Credenciais Google do tenant salvas!', 'success');
      setSecretConfigured(!!clientSecret || secretConfigured);
      setClientSecret('');
    } catch (err: any) {
      showToast('Erro: ' + (err?.message || 'Desconhecido'), 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  const configured = !!clientId && secretConfigured;

  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: '24px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill={T.primary}>
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        </svg>
        <h3 style={{ margin: 0, fontSize: T.textLg, fontWeight: T.weightSemibold, color: T.text }}>
          Google OAuth do Tenant
        </h3>
        <span style={{
          fontSize: T.textXs, padding: '2px 8px', borderRadius: 6,
          background: configured ? '#1a3a2a' : '#3a2a1a',
          color: configured ? '#4ade80' : '#fbbf24',
          fontWeight: T.weightSemibold,
        }}>
          {configured ? '✅ Configurado' : '⚠️ Não configurado'}
        </span>
      </div>

      <p style={{ fontSize: T.textSm, color: T.textMuted, margin: '0 0 16px' }}>
        Credenciais Google OAuth para login com Google na sua plataforma.
      </p>

      <div style={{
        background: T.bgActive, borderRadius: 8, padding: '12px 14px', marginBottom: 16,
        fontSize: T.textSm, color: T.textSecondary, lineHeight: 1.6,
      }}>
        <strong style={{ color: T.text }}>Como configurar:</strong><br/>
        1. Acesse <a href="https://console.cloud.google.com/apis/credentials" target="_blank" style={{ color: T.primary }}>Google Cloud Console → Credentials</a><br/>
        2. Crie um OAuth 2.0 Client ID (tipo "Web application")<br/>
        3. Adicione como Authorized redirect URI: <code style={{ background: T.bg, padding: '2px 6px', borderRadius: 4 }}>https://&#123;seu-dominio&#125;/auth/google/callback</code><br/>
        4. Copie o Client ID e Client Secret abaixo
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: T.textSm, color: T.textSecondary, fontWeight: T.weightMedium, marginBottom: 6 }}>
          Google Client ID
        </label>
        <input
          type="text"
          value={clientId}
          onInput={(e) => setClientId((e.target as HTMLInputElement).value)}
          placeholder="xxxxx.apps.googleusercontent.com"
          style={{
            width: '100%', padding: '10px 12px', fontSize: T.textMd, color: T.text,
            background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 8,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: T.textSm, color: T.textSecondary, fontWeight: T.weightMedium, marginBottom: 6 }}>
          Google Client Secret
        </label>
        <input
          type="password"
          value={clientSecret}
          onInput={(e) => setClientSecret((e.target as HTMLInputElement).value)}
          placeholder={secretConfigured ? '••• configurado (deixe vazio para manter)' : 'Não configurado'}
          style={{
            width: '100%', padding: '10px 12px', fontSize: T.textMd, color: T.text,
            background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 8,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      <Button variant="ghost" onClick={handleSave} disabled={saving} style={{
        padding: '10px 24px', background: T.primary, color: T.text, border: 'none',
        borderRadius: 8, fontWeight: T.weightSemibold, cursor: saving ? 'not-allowed' : 'pointer',
        opacity: saving ? 0.6 : 1,
      }}>
        {saving ? 'Salvando...' : 'Salvar credenciais do tenant'}
      </Button>
    </div>
  );
}

export function AdminAuth() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, SettingMeta>>({});
  const [changes, setChanges] = useState<Record<string, string>>({});

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    setLoading(true);
    try {
      const res = await api.get<any>('/admin/auth-settings');
      setSettings(res?.settings || res?.data?.settings || {});
    } catch (err: any) {
      showToast('Erro ao carregar configurações: ' + (err?.message || 'Desconhecido'), 'error');
    } finally {
      setLoading(false);
    }
  }

  function getValue(key: string): string {
    if (key in changes) return changes[key];
    const s = settings[key];
    if (!s) return '';
    if (s.encrypted && s.hasValue && !(key in changes)) return '***';
    return s.value || '';
  }

  function setValue(key: string, val: string) {
    setChanges(prev => ({ ...prev, [key]: val }));
  }

  function getBool(key: string): boolean {
    const v = key in changes ? changes[key] : settings[key]?.value;
    return v === 'true';
  }

  function toggleBool(key: string) {
    const current = getBool(key);
    setValue(key, current ? 'false' : 'true');
  }

  async function handleSave() {
    if (Object.keys(changes).length === 0) {
      showToast('Nenhuma alteração para salvar', 'info');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/auth-settings', { settings: changes });
      showToast('Configurações salvas com sucesso!', 'success');
      setChanges({});
      await loadSettings();
    } catch (err: any) {
      showToast('Erro ao salvar: ' + (err?.message || 'Desconhecido'), 'error');
    } finally {
      setSaving(false);
    }
  }

  // Verificar se todos os métodos de login estão desabilitados
  const allDisabled = !getBool('google_enabled') && !getBool('magic_link_enabled') && !getBool('password_login_enabled');

  const hasChanges = Object.keys(changes).length > 0;

  // Componentes auxiliares
  const Card = ({ title, children, icon }: { title: string; children: any; icon?: any }) => (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: '24px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        {icon && <span style={{ color: T.primary, display: 'flex' }}>{icon}</span>}
        <h3 style={{ margin: 0, fontSize: T.textLg, fontWeight: T.weightSemibold, color: T.text }}>{title}</h3>
      </div>
      {children}
    </div>
  );

  const Toggle = ({ label, value, onChange, info }: { label: string; value: boolean; onChange: () => void; info?: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
      <div>
        <div style={{ fontSize: T.textMd, color: T.text, fontWeight: T.weightMedium }}>{label}</div>
        {info && <div style={{ fontSize: T.textSm, color: T.textMuted, marginTop: 2 }}>{info}</div>}
      </div>
      <div
        onClick={onChange}
        style={{
          width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
          background: value ? T.primary : T.bgActive,
          transition: 'background 0.2s', position: 'relative', flexShrink: 0,
        }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          position: 'absolute', top: 3, left: value ? 23 : 3,
          transition: 'left 0.2s', boxShadow: `0 1px 3px ${T.blackAlpha30}`,
        }} />
      </div>
    </div>
  );

  const Input = ({ label, value, onChange, type = 'text', placeholder, info }: {
    label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; info?: string;
  }) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: T.textSm, color: T.textSecondary, fontWeight: T.weightMedium, marginBottom: 6 }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 12px', fontSize: T.textMd, color: T.text,
          background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 8,
          outline: 'none', boxSizing: 'border-box',
        }}
      />
      {info && <div style={{ fontSize: T.textXs, color: T.textMuted, marginTop: 4 }}>{info}</div>}
    </div>
  );

  const TextArea = ({ label, value, onChange, placeholder, info }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string; info?: string;
  }) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: T.textSm, color: T.textSecondary, fontWeight: T.weightMedium, marginBottom: 6 }}>
        {label}
      </label>
      <Textarea
        value={value}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        placeholder={placeholder}
        rows={3}
        style={{
          width: '100%', padding: '10px 12px', fontSize: T.textMd, color: T.text,
          background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 8,
          outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit',
        }}
      />
      {info && <div style={{ fontSize: T.textXs, color: T.textMuted, marginTop: 4 }}>{info}</div>}
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ fontSize: T.text2xl, color: T.text, marginBottom: 24, fontWeight: T.weightBold }}>Autenticação</h1>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12,
            padding: 24, marginBottom: 16, height: 120,
            animation: 'pulse 1.5s infinite',
          }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: T.text2xl, color: T.text, margin: '0 0 4px', fontWeight: T.weightBold }}>
            Autenticação
          </h1>
          <p style={{ fontSize: T.textSm, color: T.textMuted, margin: 0 }}>
            Configure métodos de login, segurança e proteção anti-bot
          </p>
        </div>
        {hasChanges && (
          <Button variant="ghost"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 24px', fontSize: T.textMd, fontWeight: T.weightSemibold,
              background: T.primary, color: T.text, border: 'none', borderRadius: 8,
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        )}
      </div>

      {/* Aviso: todos os métodos desabilitados */}
      {allDisabled && (
        <div style={{
          background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
          borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          fontSize: T.textSm, color: T.danger, fontWeight: T.weightMedium,
        }}>
          ⚠️ Todos os métodos de login estão desabilitados! Ninguém conseguirá fazer login.
        </div>
      )}

      {/* Google OAuth do Tenant */}
      <TenantGoogleOAuth />

      {/* 2. Magic Link */}
      <Card title="Magic Link" icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/>
        </svg>
      }>
        <Toggle label="Habilitado" value={getBool('magic_link_enabled')} onChange={() => toggleBool('magic_link_enabled')} info="Envia link de acesso por email" />
      </Card>

      {/* 3. Login com Senha */}
      <Card title="Login com Senha" icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
      }>
        <Toggle label="Habilitado" value={getBool('password_login_enabled')} onChange={() => toggleBool('password_login_enabled')} info="Permite login com email + senha" />
      </Card>

      {/* 4. Turnstile */}
      <Card title="Turnstile (Cloudflare)" icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      }>
        <Input label="Site Key" value={getValue('turnstile_site_key')} onChange={(v) => setValue('turnstile_site_key', v)} placeholder="1x00000000000000000000AA" />
        <Input label="Secret Key" value={getValue('turnstile_secret_key')} onChange={(v) => setValue('turnstile_secret_key', v)} type="password" placeholder={settings.turnstile_secret_key?.hasValue ? '••• configurado' : 'Não configurado'} />
        <div style={{ fontSize: T.textXs, color: T.textMuted, marginTop: 4 }}>Proteção anti-bot nos formulários de login</div>
      </Card>

      {/* 5. Autenticação em Dois Fatores (2FA) */}
      <Card title="Autenticação em Dois Fatores (2FA)" icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
          <circle cx="12" cy="16" r="1"/>
        </svg>
      }>
        <Toggle
          label="Obrigar 2FA para todos os usuários"
          value={getBool('require_2fa')}
          onChange={() => toggleBool('require_2fa')}
          info="Quando ativo, usuários sem 2FA serão redirecionados para configurar após o login"
        />
      </Card>

      {/* 6. Segurança */}
      <Card title="Segurança" icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9"/>
        </svg>
      }>
        <Toggle label="Cadastro aberto" value={getBool('signup_enabled')} onChange={() => toggleBool('signup_enabled')} info="Permite que novos usuários se cadastrem" />
        <Input label="Duração da sessão (horas)" value={getValue('session_ttl_hours')} onChange={(v) => setValue('session_ttl_hours', v)} type="number" info="Padrão: 720 horas (30 dias)" />
        <Input label="Rate limit Magic Link (req/min)" value={getValue('rate_limit_magic_link')} onChange={(v) => setValue('rate_limit_magic_link', v)} type="number" />
        <Input label="Rate limit Login (req/min)" value={getValue('rate_limit_login')} onChange={(v) => setValue('rate_limit_login', v)} type="number" />
        <TextArea label="Domínios bloqueados" value={getValue('blocked_email_domains')} onChange={(v) => setValue('blocked_email_domains', v)} placeholder="tempmail.com, guerrillamail.com" info="Emails desses domínios serão bloqueados no cadastro" />
      </Card>

      {/* Botão salvar fixo no final */}
      {hasChanges && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="ghost"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 32px', fontSize: T.textMd, fontWeight: T.weightSemibold,
              background: T.primary, color: T.text, border: 'none', borderRadius: 8,
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </div>
      )}

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}
